import {
  createContext,
  FC,
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';

interface AgentsSidePanelContextType {
  isVisible: boolean;
  taskAgentId: string | undefined;
  chatId: string | undefined;
  showAgentsSidePanel: (taskAgentId?: string, chatId?: string) => void;
  hideAgentsSidePanel: () => void;
  setChatId: (chatId: string | undefined) => void;
}

export const AgentsSidePanelContext = createContext<
  AgentsSidePanelContextType | undefined
>(undefined);

export const useAgentsSidePanel = (): AgentsSidePanelContextType => {
  const context = useContext(AgentsSidePanelContext);

  if (!context) {
    throw new Error(
      'useAgentsSidePanel must be used within an AgentsSidePanelProvider',
    );
  }

  return context;
};

export const AgentsSidePanelProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [taskAgentId, setTaskAgentId] = useState<string | undefined>(undefined);
  const [chatId, setChatId] = useState<string | undefined>(undefined);

  const showAgentsSidePanel = useCallback((id?: string, chId?: string) => {
    setTaskAgentId(id);
    setChatId(chId);
    setIsVisible(true);
  }, []);

  const hideAgentsSidePanel = useCallback(() => {
    setIsVisible(false);
    setTaskAgentId(undefined);
    setChatId(undefined);
  }, []);

  const value: AgentsSidePanelContextType = useMemo(() => {
    return {
      isVisible,
      taskAgentId,
      chatId,
      showAgentsSidePanel,
      hideAgentsSidePanel,
      setChatId,
    };
  }, [
    isVisible,
    taskAgentId,
    chatId,
    showAgentsSidePanel,
    hideAgentsSidePanel,
  ]);

  return (
    <AgentsSidePanelContext.Provider value={value}>
      {children}
    </AgentsSidePanelContext.Provider>
  );
};
